import { ComponentPropsWithRef, ComponentRef, Ref } from "react"
import { useSearchParams } from "next/dist/client/components/navigation"
// eslint-disable-next-line no-restricted-imports
import NextLink from "next/link"

import { addEmbeddedParams } from "@/lib/auth-utils"
import { routeChangeEmitter } from "@/lib/embedded/route-change-emitter"
import { isEmbedded } from "@/lib/is-embedded"
import { hasShareUriScheme } from "@/lib/route-utils"

export interface LinkProps extends ComponentPropsWithRef<typeof NextLink> {}

/*
 * Viewport prefetch is off by default: it would load the chunk of every page a visible link points to. Hover and
 * touch still prefetch the page's JS chunk, which is what makes the click feel instant. With
 * `experimental.proxyPrefetch: "strict"` (next.config.js) a prefetch never requests page data or reaches the proxy,
 * so the click always runs the auth checks fresh.
 */
export function Link({
  ref,
  ...inProps
}: LinkProps & { ref?: Ref<ComponentRef<typeof NextLink>> }) {
  const searchParams = useSearchParams()
  // add embedded auth to links that open a sign-in modal e.g. Saved
  const href = addEmbeddedParams(
    inProps.href?.toString(),
    searchParams ?? new URLSearchParams()
  )
  const props = {
    ...inProps,
    href: href,
  }

  return (
    <NextLink
      // viewport prefetch only when explicitly set to true
      prefetch={false}
      {...props}
      ref={ref}
      onClick={(e) => {
        if (props.onClick) {
          props.onClick(e)
        }
        if (
          !e.defaultPrevented &&
          !e.isPropagationStopped() &&
          isEmbedded() &&
          !props.target &&
          !props.download &&
          props.href &&
          !hasShareUriScheme(props.href.toString())
        ) {
          e.preventDefault()
          e.stopPropagation()
          routeChangeEmitter.emit(props.href.toString())
        }
      }}
    />
  )
}
Link.displayName = "Link"
